/**
 * Group a day's crew assignments into absolute time windows, per crew
 * member, so every surface can ask "what is this person on, and how far
 * along are they?".
 *
 * The one thing that genuinely differs per platform is how a wall-clock
 * assignment (DATE + TIME, no zone) becomes an instant: the producer web
 * app reads it in the *festival's* timezone, because a producer may be
 * watching from another country, while the crew app uses device-local
 * time, because the phone is on site. That difference is a single
 * injected function here rather than a forked copy of this module — the
 * grouping, the guards and the midnight rules stay in one place.
 *
 * Feed the result to selectCurrentShift / selectCurrentAndNext /
 * computeShiftProgress in crewShiftProgress.ts.
 */

import type { ShiftWindow } from "./crewShiftProgress";
import { BRIEFING_TZ_DEFAULT, wallClockToUtc } from "./briefingTime";
import { effectiveShiftEndParts, effectiveShiftStartParts } from "./effectiveShift";

/** The columns needed off a crew_assignments row. */
export interface ShiftWindowSource {
  crew_member_id: string | null;
  event_date: string;
  filming_start: string | null;
  filming_end: string | null;
  is_active?: boolean | null;
}

/**
 * Turns one assignment's wall-clock times into an absolute window.
 * Supplied by the caller — see the module comment for why.
 *
 * Takes the whole triple rather than one time at a time, because the
 * midnight rules need it: where a shift's end lands depends on where its
 * start did (23:00 → 01:00 ends the next day).
 */
export type ShiftWindowResolver = (
  eventDate: string,
  filmingStart: string,
  filmingEnd: string,
) => ShiftWindow | null;

/** An assignment paired with the absolute window it occupies. */
export interface TimedAssignment<T extends ShiftWindowSource> {
  assignment: T;
  window: ShiftWindow;
}

/**
 * The absolute window one assignment occupies, or null when the row
 * can't be placed on a clock — no crew member, no times, or explicitly
 * inactive. An assignment nobody is on can't drive anyone's ring.
 */
export function assignmentWindow(
  assignment: ShiftWindowSource,
  resolve: ShiftWindowResolver,
): ShiftWindow | null {
  if (!assignment.crew_member_id) return null;
  if (assignment.is_active === false) return null;
  if (!assignment.event_date || !assignment.filming_start || !assignment.filming_end) return null;

  const window = resolve(assignment.event_date, assignment.filming_start, assignment.filming_end);
  if (!window) return null;
  if (!Number.isFinite(window.startMs) || !Number.isFinite(window.endMs)) return null;

  return window;
}

/**
 * Group assignments per crew member, keeping each row attached to its
 * window and sorted by start time.
 *
 * The crew list needs the assignment itself — its title and times — not
 * just the window, so this is the primitive; the windows-only variant
 * below is derived from it.
 */
export function buildTimedAssignmentsByCrewMember<T extends ShiftWindowSource>(
  assignments: readonly T[] | null | undefined,
  resolve: ShiftWindowResolver,
): Map<string, TimedAssignment<T>[]> {
  const byCrewMember = new Map<string, TimedAssignment<T>[]>();
  if (!assignments) return byCrewMember;

  for (const assignment of assignments) {
    const window = assignmentWindow(assignment, resolve);
    if (!window || !assignment.crew_member_id) continue;

    const entry = { assignment, window };
    const existing = byCrewMember.get(assignment.crew_member_id);
    if (existing) existing.push(entry);
    else byCrewMember.set(assignment.crew_member_id, [entry]);
  }

  for (const entries of byCrewMember.values()) {
    entries.sort((a, b) => a.window.startMs - b.window.startMs);
  }

  return byCrewMember;
}

/** Windows only, for callers that just need the progress ring. */
export function buildShiftWindowsByCrewMember(
  assignments: readonly ShiftWindowSource[] | null | undefined,
  resolve: ShiftWindowResolver,
): Map<string, ShiftWindow[]> {
  const byCrewMember = new Map<string, ShiftWindow[]>();
  buildTimedAssignmentsByCrewMember(assignments, resolve).forEach((entries, crewMemberId) => {
    byCrewMember.set(
      crewMemberId,
      entries.map((entry) => entry.window),
    );
  });
  return byCrewMember;
}

/**
 * Read a shift's wall-clock times in the briefing's timezone — the
 * resolver for every PRODUCER surface (web and the producer app): a
 * producer may be watching from another country, so a shift must never
 * move by their own offset. The effectiveShift*Parts helpers apply the
 * other trap first: a 23:00 → 01:00 shift ends the next day, and a
 * shift starting before 06:00 belongs to the night after event_date.
 *
 * Bind it once per briefing and hand it to
 * buildTimedAssignmentsByCrewMember / assignmentWindow. (The crew app
 * injects a device-local resolver into that same code instead.)
 */
export function briefingShiftWindowResolver(
  timezone: string = BRIEFING_TZ_DEFAULT,
): ShiftWindowResolver {
  return (eventDate, filmingStart, filmingEnd): ShiftWindow => {
    const startParts = effectiveShiftStartParts(eventDate, filmingStart);
    const endParts = effectiveShiftEndParts(eventDate, filmingStart, filmingEnd);
    return {
      startMs: wallClockToUtc(startParts.date, startParts.time, timezone).getTime(),
      endMs: wallClockToUtc(endParts.date, endParts.time, timezone).getTime(),
    };
  };
}
